import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash, FaArrowLeft } from "react-icons/fa";
import ErrorNotification from "./ErrorNotification";
import "./Register.css";
import API_BASE_URL from '../apiConfig.js';
import SEO from './SEO';

export default function Register() {
  const navigate  = useNavigate();
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    nombre:             "",
    apellido:           "",
    correo:             "",
    telefono:           "",
    fechaNacimiento:    "",
    password:           "",
    passwordConfirm:    "",
    contactoEmergencia: "",
    responsivaFirmada:  false,
    tipoSangre:         "",
    enfermedades:       "",
    lesiones:           "",
  });

  const [showPass, setShowPass]             = useState(false);
  const [errors, setErrors]                 = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showResponsiva, setShowResponsiva] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    const e = {};
    if (!form.nombre.trim())                    e.nombre            = "El nombre es obligatorio.";
    if (!form.apellido.trim())                  e.apellido          = "El apellido es obligatorio.";
    if (!form.correo.includes("@"))             e.correo            = "Correo inválido.";
    if (form.password.length < 8)               e.password          = "Mínimo 8 caracteres.";
    if (form.password !== form.passwordConfirm) e.passwordConfirm   = "Las contraseñas no coinciden.";
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setShowErrorPopup(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    // Mostrar responsiva PRIMERO, antes de cualquier validación
    if (!form.responsivaFirmada) {
      setShowResponsiva(true);
      return;
    }
    if (!validate()) return;
    setLoading(true);

    const userToSave = {
      nombre:             form.nombre.trim(),
      apellido:           form.apellido.trim(),
      email:              form.correo.toLowerCase().trim(),
      password:           form.password,
      telefono:           form.telefono,
      contactoEmergencia: form.contactoEmergencia,
      fechaNacimiento:    form.fechaNacimiento || null,
      tipoSangre:         form.tipoSangre || null,
      alergias:           form.enfermedades || null,
      lesiones:           form.lesiones || null,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/signup`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(userToSave),
      });

      const data = await response.json();

      if (response.ok) {
        const { user, token } = data;

        localStorage.setItem("booz_token", token);
        localStorage.setItem("booz_user", JSON.stringify({
          id:                  user.id,
          nombre:              user.nombre,
          apellido:            user.apellido,
          email:               user.email,
          role:                user.role || "cliente",
          creditosDisponibles: user.creditosDisponibles || 0,
          tipoCliente:         user.tipoCliente,
          suscripcionActiva:   user.suscripcionActiva,
        }));

        navigate("/cliente/inicio");

      } else {
        setErrors({ general: data.error || "El email ya está registrado." });
        setShowErrorPopup(true);
      }

    } catch (err) {
      setErrors({ general: "Error de conexión con el servidor Booz." });
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <SEO title="Crear cuenta" description="Crea tu cuenta en BOOZ Studio y empieza a reservar tus clases de Pilates." />

      {/* Botón de regreso */}
      <Link to="/login" className="back-button" aria-label="Volver al inicio de sesión">
        <FaArrowLeft />
        <span>Iniciar sesión</span>
      </Link>

      <h2 className="auth-title">Registro</h2>
      <p className="auth-subtitle">Crea tu cuenta en Booz Studio</p>

      {showErrorPopup && (
        <ErrorNotification errors={errors} onClose={() => setShowErrorPopup(false)} />
      )}

      <form onSubmit={handleSubmit} noValidate>

        {/* Datos personales */}
        <p className="form-section-label">Datos personales</p>

        <div className="input-row">
          <div className="input-group">
            <label htmlFor="nombre">Nombre</label>
            <input id="nombre" name="nombre" className="auth-input"
              value={form.nombre} onChange={handleChange} required />
          </div>
          <div className="input-group">
            <label htmlFor="apellido">Apellido</label>
            <input id="apellido" name="apellido" className="auth-input"
              value={form.apellido} onChange={handleChange} required />
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="correo">Correo electrónico</label>
          <input id="correo" name="correo" type="email" className="auth-input"
            placeholder="tu@correo.com"
            value={form.correo} onChange={handleChange} required />
        </div>

        <div className="input-row">
          <div className="input-group">
            <label htmlFor="telefono">Teléfono</label>
            <input id="telefono" name="telefono" className="auth-input"
              placeholder="55 1234 5678"
              value={form.telefono} onChange={handleChange} />
          </div>
          <div className="input-group">
            <label htmlFor="fechaNacimiento">Fecha de nacimiento</label>
            <input id="fechaNacimiento" name="fechaNacimiento" type="date"
              className="auth-input"
              value={form.fechaNacimiento} onChange={handleChange} />
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="contactoEmergencia">Contacto de emergencia</label>
          <input id="contactoEmergencia" name="contactoEmergencia" className="auth-input"
            placeholder="Nombre y teléfono"
            value={form.contactoEmergencia} onChange={handleChange} />
        </div>

        {/* Seguridad */}
        <p className="form-section-label">Seguridad</p>

        <div className="input-group">
          <label htmlFor="password">Contraseña</label>
          <div className="password-wrapper">
            <input id="password" name="password"
              type={showPass ? "text" : "password"}
              className="auth-input"
              placeholder="Mínimo 8 caracteres"
              value={form.password} onChange={handleChange} required />
            <button type="button" className="toggle-pass"
              onClick={() => setShowPass(!showPass)}
              aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}>
              {showPass ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="passwordConfirm">Confirmar contraseña</label>
          <input id="passwordConfirm" name="passwordConfirm" type="password"
            className="auth-input"
            placeholder="Repite tu contraseña"
            value={form.passwordConfirm} onChange={handleChange} required />
        </div>

        {/* Información médica */}
        <p className="form-section-label">Información médica</p>

        <div className="input-group">
          <label htmlFor="tipoSangre">Tipo de sangre</label>
          <select id="tipoSangre" name="tipoSangre"
            className="auth-input auth-select"
            value={form.tipoSangre} onChange={handleChange}>
            <option value="">Selecciona...</option>
            {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="enfermedades">Enfermedades o condiciones médicas</label>
          <textarea id="enfermedades" name="enfermedades"
            className="auth-input auth-textarea"
            placeholder="Ej. diabetes, hipertensión... (opcional)"
            value={form.enfermedades} onChange={handleChange} rows={2} />
        </div>

        <div className="input-group">
          <label htmlFor="lesiones">Lesiones o limitaciones físicas</label>
          <textarea id="lesiones" name="lesiones"
            className="auth-input auth-textarea"
            placeholder="Ej. lesión de rodilla, lumbalgia... (opcional)"
            value={form.lesiones} onChange={handleChange} rows={2} />
        </div>

        {/* Responsiva */}
        <p className="responsiva-aviso-texto">
          Al registrarte aceptarás la{" "}
          <button type="button"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: '#8FD9FB', fontWeight: 800, textDecoration: 'underline' }}
            onClick={() => setShowResponsiva(true)}>
            responsiva de participación
          </button>{" "}de Booz Studio.
        </p>

        <button id="btn-registrarme" className="auth-button" type="submit" disabled={loading}>
          {loading ? "Creando cuenta..." : "Registrarme"}
        </button>

      </form>

      <div className="auth-footer">
        <p className="auth-secondary-text">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="auth-link-highlight">
            Iniciar sesión
          </Link>
        </p>
      </div>

      {/* ── MODAL RESPONSIVA ── */}
      {showResponsiva && ReactDOM.createPortal(
        <div className="responsiva-overlay" onClick={e => e.target === e.currentTarget && setShowResponsiva(false)}>
          <div className="responsiva-modal">

            <div className="responsiva-header">
              <h3>Responsiva de Participación</h3>
              <button className="responsiva-close" onClick={() => setShowResponsiva(false)}>✕</button>
            </div>

            <div className="responsiva-body">
              <p className="responsiva-estudio">BOOZ Studio</p>

              <p>Al inscribirme y participar en las clases de BOOZ Studio, me comprometo a realizar el ejercicio de manera consciente, prestando atención a mi cuerpo y siguiendo en todo momento las indicaciones de la instructora.</p>

              <p>Declaro que es mi responsabilidad informar previamente sobre cualquier lesión, condición médica o limitación física, con el fin de que sea considerada durante la ejecución de la rutina.</p>

              <p>Asimismo, me comprometo a comunicar de inmediato cualquier molestia, dolor o incomodidad que surja durante la clase.</p>

              <p>Entiendo que mi bienestar depende también de escuchar a mi cuerpo y respetar sus límites.</p>

              <div className="responsiva-firma-box">
                <div className="responsiva-firma-row">
                  <span className="responsiva-firma-label">Nombre</span>
                  <span className="responsiva-firma-valor">
                    {(form.nombre || form.apellido) ? `${form.nombre} ${form.apellido}`.trim() : '____________________'}
                  </span>
                </div>
                <div className="responsiva-firma-row">
                  <span className="responsiva-firma-label">Firma</span>
                  <span className="responsiva-firma-valor responsiva-firma-check">
                    ✓ Aceptación digital — botón "Acepto y registrarme"
                  </span>
                </div>
                <div className="responsiva-firma-row">
                  <span className="responsiva-firma-label">Fecha</span>
                  <span className="responsiva-firma-valor">
                    {new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>
            </div>

            <div className="responsiva-footer">
              <button className="responsiva-btn-cancel" onClick={() => setShowResponsiva(false)}>
                Cancelar
              </button>
              <button className="responsiva-btn-accept" onClick={() => {
                setForm(f => ({ ...f, responsivaFirmada: true }));
                setShowResponsiva(false);
                setTimeout(() => document.getElementById('btn-registrarme')?.click(), 100);
              }}>
                Acepto y registrarme
              </button>
            </div>

          </div>
        </div>
      , document.body)}

    </div>
  );
}